import { default as React } from 'react';
import { Content, Header, Page } from '@backstage/core-components';
import {
  CatalogTable,
  CatalogTableColumnsFunc,
  CatalogTableRow,
} from '@backstage/plugin-catalog';
import {
  EntityListProvider,
  useEntityList,
  EntityKindPicker,
  EntityFilter,
  DefaultEntityFilters,
} from '@backstage/plugin-catalog-react';
import { Entity } from '@backstage/catalog-model';
import { makeStyles } from '@mui/styles';
import { Button, Box } from '@material-ui/core';
import { Alert } from '@mui/lab';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import AddIcon from '@mui/icons-material/Add';
import PublicIcon from '@mui/icons-material/Public';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router-dom';
import { createColumnsFunc } from '../microapps/columns';
import {
  useCurrentUserEmail,
  useSkyStageAdminEmails,
  isUserAuthorizedForEntity,
} from '../microapps/hooks';
import { CatalogViewToggle, ViewMode } from '../CatalogViewToggle';
import { usePersistentViewMode } from '../usePersistentViewMode';
import { PublicAppDeleteButton } from './PublicAppDeleteButton';
import { PublicAppSearchBar } from './PublicAppSearchBar';
import { PublicAppCardGrid, PublicAppEmptyState } from './PublicAppCardGrid';
import { getPublicAppOwners, PublicAppSearchFilter } from './publicAppFilters';

// ── Constants ─────────────────────────────────────────────────────────────────

const PUBLIC_APP_TYPE_ANNOTATION = 'skystage.wellsky.com/type';
const PUBLIC_APP_TYPE_VALUE = 'public-microapp';
const PUBLIC_APP_URL_ANNOTATION = 'skystage.io/hosted-app-url';

// ── Filter ────────────────────────────────────────────────────────────────────

class PublicAppFilter implements EntityFilter {
  filterEntity(entity: Entity): boolean {
    return (
      entity.metadata.annotations?.[PUBLIC_APP_TYPE_ANNOTATION] ===
      PUBLIC_APP_TYPE_VALUE
    );
  }
}

type PublicAppFilters = DefaultEntityFilters & {
  publicAppType?: PublicAppFilter;
  /** IDP-1291: one search term matched against Name, Owner and Description. */
  publicAppSearch?: PublicAppSearchFilter;
};

/** localStorage key for the remembered card/table layout (IDP-1291). */
const VIEW_MODE_STORAGE_KEY = 'skystage.publicApps.viewMode';

const PublicAppsOnlyFilter = () => {
  const { updateFilters } = useEntityList<PublicAppFilters>();

  React.useEffect(() => {
    updateFilters({ publicAppType: new PublicAppFilter() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};

// ── Columns ───────────────────────────────────────────────────────────────────

export const publicAppsColumnsFunc: CatalogTableColumnsFunc =
  entityListContext => {
    const columns = createColumnsFunc(['System', 'Lifecycle', 'Tags', 'Type'])(
      entityListContext,
    );

    // Find the Name column and make it clickable to open the public app URL.
    // Row click (handled via CatalogTable actions) opens the entity details page.
    const nameColumnIndex = columns.findIndex(col => {
      const field = typeof col.field === 'string' ? col.field : '';
      return (
        field === 'entity.metadata.name' ||
        field === 'resolved.entityRef' ||
        field === 'resolved.name'
      );
    });

    if (nameColumnIndex !== -1) {
      const originalNameColumn = columns[nameColumnIndex];
      columns[nameColumnIndex] = {
        ...originalNameColumn,
        render: (rowData: CatalogTableRow) => {
          try {
            const entity = rowData.entity;
            const appName =
              entity?.metadata?.title || entity?.metadata?.name || '';
            const appUrl =
              entity?.metadata?.annotations?.[PUBLIC_APP_URL_ANNOTATION];

            if (appUrl) {
              return (
                <a
                  href={appUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: '#1976d2',
                    textDecoration: 'none',
                    fontWeight: 500,
                  }}
                  onClick={e => e.stopPropagation()}
                  onMouseOver={e => {
                    e.currentTarget.style.textDecoration = 'underline';
                  }}
                  onMouseOut={e => {
                    e.currentTarget.style.textDecoration = 'none';
                  }}
                  onFocus={e => {
                    e.currentTarget.style.textDecoration = 'underline';
                  }}
                  onBlur={e => {
                    e.currentTarget.style.textDecoration = 'none';
                  }}
                >
                  {appName}
                </a>
              );
            }

            return originalNameColumn.render
              ? originalNameColumn.render(rowData, 'row')
              : appName;
          } catch {
            return originalNameColumn.render
              ? originalNameColumn.render(rowData, 'row')
              : null;
          }
        },
      };
    }

    // IDP-1291: the Owner column shows the app's owner emails
    // (microapp/owners) — the same values the cards show and the Owner filter
    // searches. Falls back to the catalog owner when the annotation is missing.
    const ownerColumnIndex = columns.findIndex(
      col => col.field === 'resolved.ownedByRelationsTitle',
    );
    if (ownerColumnIndex !== -1) {
      columns[ownerColumnIndex] = {
        ...columns[ownerColumnIndex],
        render: (rowData: CatalogTableRow) => {
          const owners = getPublicAppOwners(rowData.entity);
          return owners.length > 0 ? owners.join(', ') : '-';
        },
        // Sort by the emails shown, not the hidden catalog owner.
        customSort: (a: CatalogTableRow, b: CatalogTableRow) =>
          getPublicAppOwners(a.entity)
            .join(',')
            .localeCompare(getPublicAppOwners(b.entity).join(',')),
      };
    }

    return columns;
  };

// ── Styles ────────────────────────────────────────────────────────────────────

const useStyles = makeStyles(theme => ({
  tableWrapper: {
    '& .MuiPaper-root': {
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      borderRadius: theme.spacing(1),
      overflow: 'hidden',
    },
    '& .MuiTableHead-root': {
      '& .MuiTableCell-head': {
        backgroundColor: theme.palette.mode === 'dark' ? '#1f1f1f' : '#f5f7fa',
        fontWeight: 600,
        fontSize: '0.875rem',
        color: theme.palette.mode === 'dark' ? '#fff' : '#333',
        borderBottom: `2px solid ${theme.palette.divider}`,
        padding: '16px',
      },
    },
    '& .MuiTableBody-root': {
      '& .MuiTableRow-root': {
        transition: 'all 0.2s ease-in-out',
        cursor: 'pointer',
        '&:hover': {
          backgroundColor:
            theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.05)'
              : 'rgba(0, 0, 0, 0.02)',
          transform: 'translateY(-1px)',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
        },
        '& .MuiTableCell-body': {
          padding: '16px',
          fontSize: '0.875rem',
          borderBottom: `1px solid ${theme.palette.divider}`,
        },
      },
    },
    '& .MuiIconButton-root': {
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        transform: 'scale(1.1)',
      },
    },
  },
  alertBox: {
    flex: 1,
    marginRight: theme.spacing(2),
    backgroundColor: 'transparent',
    border: `1px solid ${theme.palette.info.main}30`,
    borderRadius: theme.spacing(1),
    minWidth: 0,
    '& .MuiAlert-icon': {
      color: theme.palette.info.main,
      flexShrink: 0,
    },
    '& .MuiAlert-message': {
      display: 'flex',
      alignItems: 'center',
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
  },
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'nowrap',
    gap: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
    },
  },
  buttonGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flexShrink: 0,
    [theme.breakpoints.down('md')]: {
      width: '100%',
      justifyContent: 'flex-end',
    },
  },
  deployButton: {
    textTransform: 'none',
    fontWeight: 600,
    minWidth: '180px',
    borderRadius: theme.spacing(1),
    transition: 'all 0.2s ease-in-out',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
    },
  },
}));

// ── PublicAppListing ──────────────────────────────────────────────────────────

const FIELD_NAME_PATTERNS = [
  'entity.metadata.name',
  'resolved.entityRef',
  'resolved.name',
];
const FIELD_OWNER_PATTERNS = ['resolved.ownedByRelationsTitle'];
const FIELD_DESCRIPTION_PATTERNS = ['entity.metadata.description'];

/**
 * IDP-1291: heading with one Filter box + card or table layout. Both layouts
 * read the same filtered entity list (the search filter is registered with
 * EntityListProvider), so the filter behaves identically in either layout.
 */
const PublicAppListing = ({ viewMode }: { viewMode: ViewMode }) => {
  const { entities, updateFilters } = useEntityList<PublicAppFilters>();
  const classes = useStyles();
  const navigate = useNavigate();
  const currentUserEmail = useCurrentUserEmail();
  const adminEmails = useSkyStageAdminEmails();
  const [searchTerm, setSearchTerm] = React.useState('');

  React.useEffect(() => {
    updateFilters({
      publicAppSearch: searchTerm.trim()
        ? new PublicAppSearchFilter(searchTerm)
        : undefined,
    });
  }, [searchTerm, updateFilters]);

  const clearSearch = React.useCallback(() => setSearchTerm(''), []);
  const searchActive = searchTerm.trim().length > 0;
  const count = entities.length;

  // Single "Actions" column combining Delete (owner-only) + View icons
  const columnsWithActions: CatalogTableColumnsFunc = React.useCallback(
    entityListContext => {
      const baseColumns = publicAppsColumnsFunc(entityListContext);

      // Apply explicit widths so columns are balanced
      const sizedColumns = baseColumns.map(col => {
        const field = typeof col.field === 'string' ? col.field : '';
        if (FIELD_NAME_PATTERNS.includes(field)) {
          return { ...col, width: '30%' };
        }
        if (FIELD_OWNER_PATTERNS.includes(field)) {
          return { ...col, width: '240px' };
        }
        if (FIELD_DESCRIPTION_PATTERNS.includes(field)) {
          return { ...col, width: 'auto' };
        }
        return col;
      });

      const actionsColumn = {
        title: 'Actions',
        field: 'actions-combined',
        width: '100px',
        sorting: false,
        render: (rowData: CatalogTableRow) => {
          const entity = rowData.entity;
          if (!entity?.metadata) return null;

          const namespace = entity.metadata.namespace || 'default';
          const kind = entity.kind?.toLowerCase() || 'component';
          const name = entity.metadata.name;
          const isAuthorized = isUserAuthorizedForEntity(
            entity,
            currentUserEmail,
            adminEmails,
          );

          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {isAuthorized && <PublicAppDeleteButton entity={entity} />}
              <Tooltip title="View Details">
                <IconButton
                  size="small"
                  onClick={e => {
                    e.stopPropagation();
                    navigate(`/catalog/${namespace}/${kind}/${name}`);
                  }}
                  style={{ padding: 4, opacity: 0.6 }}
                  aria-label={`view ${name}`}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
          );
        },
      };

      return [...sizedColumns, actionsColumn];
    },
    [currentUserEmail, adminEmails, navigate],
  );

  return (
    <>
      <div style={{ display: 'none' }}>
        <EntityKindPicker initialFilter="component" />
        <PublicAppsOnlyFilter />
      </div>

      <PublicAppSearchBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        count={count}
      />

      {viewMode === 'cards' ? (
        <PublicAppCardGrid
          searchActive={searchActive}
          onClearSearch={clearSearch}
        />
      ) : (
        <div className={classes.tableWrapper}>
          <CatalogTable
            columns={columnsWithActions}
            actions={[]}
            // The heading above shows the title, count and the one Filter box
            // for both layouts, so the table's own toolbar (title + search)
            // is hidden.
            tableOptions={{ search: false, toolbar: false }}
            emptyContent={
              <PublicAppEmptyState
                searchActive={searchActive}
                onClearSearch={clearSearch}
              />
            }
          />
        </div>
      )}
    </>
  );
};

// ── PublicAppPage ──────────────────────────────────────────────────────────────

export const PublicAppPage = () => {
  const navigate = useNavigate();
  const classes = useStyles();
  // IDP-1291: cards by default; the user's choice is remembered across reloads.
  const [viewMode, setViewMode] = usePersistentViewMode(
    VIEW_MODE_STORAGE_KEY,
    'cards',
  );

  return (
    <Page themeId="home">
      <Header
        title="Public Hosted Apps"
        subtitle="Internet-facing apps secured with WellSky Identity (CIAM)"
      />
      <Content>
        <Box mb={3} className={classes.headerContainer}>
          <Alert severity="info" className={classes.alertBox}>
            <PublicIcon style={{ fontSize: 16, marginRight: 6 }} />
            These apps are publicly accessible on the internet and protected by
            WellSky Identity (CIAM) authentication via{' '}
            <strong>oauth2-proxy</strong>. Only invited users or group members
            can sign in.
          </Alert>
          <Box className={classes.buttonGroup}>
            <Button
              variant="contained"
              color="primary"
              size="medium"
              startIcon={<AddIcon />}
              onClick={() =>
                navigate('/create/templates/default/public-app-hosting')
              }
              className={classes.deployButton}
            >
              Deploy Public App
            </Button>
            <CatalogViewToggle viewMode={viewMode} onChange={setViewMode} />
          </Box>
        </Box>

        <EntityListProvider>
          <PublicAppListing viewMode={viewMode} />
        </EntityListProvider>
      </Content>
    </Page>
  );
};
